import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  listAgentConversations,
  getAgentConversation,
  deleteAgentConversation,
} from '@/api/client';
import { useAgentStream, SSECardEvent, SSEDoneEvent, SSEToolCallEvent } from '@/hooks/useAgentStream';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

/** Build a human-readable summary for a card selection. */
function _formatCardSelection(
  cardType: string,
  selection: Record<string, unknown>,
): string | null {
  switch (cardType) {
    case 'customer_selector':
      return `✅ Selected customer: ${selection.customer_name || selection.customer_id || '—'}`;
    case 'product_selector': {
      const items = selection.items as Array<{ name: string; quantity: number; unit?: string }> | undefined;
      if (items && items.length > 0) {
        const list = items.map((i) => `${i.name} ×${i.quantity}`).join(', ');
        return `✅ Selected products: ${list}`;
      }
      return '✅ Products selected';
    }
    case 'confirmation':
      return selection.confirmed ? '✅ Confirmed' : '❌ Cancelled';
    case 'option_selector':
      return `✅ Selected: ${selection.label || selection.value || '—'}`;
    case 'question_card':
      return `✅ Answered: ${selection.answer || selection.custom_answer || selection.title || '—'}`;
    case 'document_builder':
      return selection.action === 'created'
        ? `✅ Created: ${selection.document_type_name || 'Document'} #${selection.document_number || '—'}`
        : '❌ Document creation cancelled';
    case 'payment':
      return `✅ Payment: ${selection.method || '—'}`;
    case 'capability_editor': {
      const customerName = selection.customer_name as string | undefined;
      const items = selection.items as Array<{ name?: string }> | undefined;
      return `✅ Draft updated${customerName ? ` for ${customerName}` : ''}${items?.length ? ` with ${items.length} items` : ''}`;
    }
    case 'insight_kpi':
    case 'insight_chart':
      return `✅ Insight acknowledged`;
    case 'create_document_cta':
      return selection.action === 'open_create_document'
        ? '✅ Opened create document draft'
        : '✅ Draft card dismissed';
    default:
      return null;
  }
}

/** Try to parse a card_response JSON stored in user messages from DB. */
function _parseCardResponseContent(content: string): { summary: string } | null {
  try {
    const parsed = JSON.parse(content);
    if (parsed?.card_response) {
      const sel = parsed.selection || {};
      const summary = _formatCardSelection(parsed.card_type || '', sel);
      return { summary: summary || 'Card response' };
    }
  } catch {
    // not JSON, normal message
  }
  return null;
}

export interface AgentMessage {
  role: 'user' | 'assistant' | 'system';
  content?: string;
  card?: SSECardEvent;
  /** Snapshot of a resolved card (read-only in history). */
  resolvedCard?: { cardType: string; data: Record<string, unknown>; selection: Record<string, unknown> };
  toolCall?: SSEToolCallEvent;
  timestamp?: string;
  isStreaming?: boolean;
}

export interface AgentConversation {
  id: string;
  title?: string;
  last_message_preview?: string;
  created_at: string;
  updated_at: string;
}

export type AgentProgressStatus = 'in_progress' | 'completed' | 'waiting';

export interface AgentProgressStep {
  key: string;
  label: string;
  status: AgentProgressStatus;
}

function _progressLabel(t: TFunction, key: string): string {
  switch (key) {
    case 'understanding_request':
      return t('agentChat.progress.steps.understandingRequest', { defaultValue: 'Understanding request' });
    case 'collecting_customer':
      return t('agentChat.progress.steps.collectingCustomer', { defaultValue: 'Collecting customer' });
    case 'collecting_items':
      return t('agentChat.progress.steps.collectingItems', { defaultValue: 'Collecting items' });
    case 'collecting_document_type':
      return t('agentChat.progress.steps.collectingDocumentType', { defaultValue: 'Collecting document type' });
    case 'collecting_payment':
      return t('agentChat.progress.steps.collectingPayment', { defaultValue: 'Collecting payment details' });
    case 'reviewing_history':
      return t('agentChat.progress.steps.reviewingHistory', { defaultValue: 'Reviewing history' });
    case 'loading_overview':
      return t('agentChat.progress.steps.loadingOverview', { defaultValue: 'Loading overview' });
    case 'loading_report':
      return t('agentChat.progress.steps.loadingReport', { defaultValue: 'Loading financial report' });
    case 'preparing_draft':
      return t('agentChat.progress.steps.preparingDraft', { defaultValue: 'Preparing draft' });
    case 'calculating_totals':
      return t('agentChat.progress.steps.calculatingTotals', { defaultValue: 'Calculating totals' });
    case 'creating_document':
      return t('agentChat.progress.steps.creatingDocument', { defaultValue: 'Creating document' });
    case 'creating_receipt':
      return t('agentChat.progress.steps.creatingReceipt', { defaultValue: 'Creating receipt' });
    case 'waiting_customer':
      return t('agentChat.progress.steps.waitingCustomer', { defaultValue: 'Waiting for customer selection' });
    case 'waiting_items':
      return t('agentChat.progress.steps.waitingItems', { defaultValue: 'Waiting for item selection' });
    case 'waiting_confirmation':
      return t('agentChat.progress.steps.waitingConfirmation', { defaultValue: 'Waiting for confirmation' });
    case 'waiting_option':
      return t('agentChat.progress.steps.waitingOption', { defaultValue: 'Waiting for selection' });
    case 'waiting_answer':
      return t('agentChat.progress.steps.waitingAnswer', { defaultValue: 'Waiting for answer' });
    case 'waiting_payment':
      return t('agentChat.progress.steps.waitingPayment', { defaultValue: 'Waiting for payment details' });
    case 'waiting_draft_review':
      return t('agentChat.progress.steps.waitingDraftReview', { defaultValue: 'Waiting for draft review' });
    default:
      return t('agentChat.progress.steps.working', { defaultValue: 'Working' });
  }
}

function _progressKeyFromTool(toolName: string): string | null {
  switch (toolName) {
    case 'search_customers':
    case 'get_customer_details':
    case 'list_all_customers':
    case 'get_multiple_customer_details':
    case 'get_multiple_customer_balances':
      return 'collecting_customer';
    case 'search_products':
    case 'get_product_details':
    case 'list_rivhit_items':
      return 'collecting_items';
    case 'list_document_types':
      return 'collecting_document_type';
    case 'list_payment_types':
      return 'collecting_payment';
    case 'list_orders':
    case 'get_customer_order_history':
    case 'get_multiple_customer_order_histories':
    case 'list_recent_documents':
      return 'reviewing_history';
    case 'get_operations_overview':
      return 'loading_overview';
    case 'get_financial_report':
      return 'loading_report';
    case 'ask_create_document_card':
    case 'ask_edit_capabilities':
    case 'ask_open_document_draft':
      return 'preparing_draft';
    case 'get_vat_rate':
      return 'calculating_totals';
    case 'create_rivhit_document':
      return 'creating_document';
    case 'create_rivhit_receipt':
      return 'creating_receipt';
    default:
      return null;
  }
}

function _progressKeyFromCard(cardType: string): string | null {
  switch (cardType) {
    case 'customer_selector':
      return 'waiting_customer';
    case 'product_selector':
      return 'waiting_items';
    case 'confirmation':
    case 'document_preview':
      return 'waiting_confirmation';
    case 'option_selector':
      return 'waiting_option';
    case 'question_card':
      return 'waiting_answer';
    case 'payment':
      return 'waiting_payment';
    case 'document_builder':
    case 'capability_editor':
    case 'create_document_cta':
      return 'waiting_draft_review';
    default:
      return null;
  }
}

function _activateProgressStep(
  prev: AgentProgressStep[],
  key: string,
  label: string,
  status: Exclude<AgentProgressStatus, 'completed'>,
): AgentProgressStep[] {
  const next = prev.map((step) =>
    step.status === 'in_progress' || step.status === 'waiting'
      ? { ...step, status: 'completed' as const }
      : step,
  );
  const existingIndex = next.findIndex((step) => step.key === key);
  if (existingIndex >= 0) {
    next[existingIndex] = { ...next[existingIndex], label, status };
    return [...next];
  }
  return [...next, { key, label, status }];
}

function _completeProgressStep(prev: AgentProgressStep[], key: string): AgentProgressStep[] {
  return prev.map((step) => (
    step.key === key && step.status !== 'completed'
      ? { ...step, status: 'completed' as const }
      : step
  ));
}

interface AgentChatContextType {
  isOpen: boolean;
  toggleOpen: () => void;
  setIsOpen: (open: boolean) => void;
  // Conversations
  conversations: AgentConversation[];
  activeConversationId: string | null;
  setActiveConversationId: (id: string | null) => void;
  loadConversations: () => Promise<void>;
  deleteConversation: (id: string) => Promise<void>;
  startNewConversation: () => void;
  // Messages
  messages: AgentMessage[];
  isStreaming: boolean;
  activeToolCall: string | null;
  progressSteps: AgentProgressStep[];
  // Actions
  sendMessage: (text: string) => Promise<void>;
  sendCardResponse: (cardId: string, cardType: string, selection: Record<string, unknown>) => Promise<void>;
  stopStreaming: () => void;
  // Pending card
  pendingCard: SSECardEvent | null;
  // Follow-up suggestions
  followUpSuggestions: string[];
}

const AgentChatContext = createContext<AgentChatContextType | null>(null);

export function AgentChatProvider({ children }: { children: React.ReactNode }) {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [conversations, setConversations] = useState<AgentConversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<AgentMessage[]>([]);
  const [activeToolCall, setActiveToolCall] = useState<string | null>(null);
  const [progressSteps, setProgressSteps] = useState<AgentProgressStep[]>([]);
  const [pendingCard, setPendingCard] = useState<SSECardEvent | null>(null);
  const [followUpSuggestions, setFollowUpSuggestions] = useState<string[]>([]);

  const { isStreaming, streamingContent, setStreamingContent, startStream, stopStream } =
    useAgentStream({
      onToken: () => {
        // Content accumulates automatically via setStreamingContent in the hook
      },
      onCard: (card) => {
        setPendingCard(card);
        setActiveToolCall(null);
        const progressKey = _progressKeyFromCard(card.card_type);
        if (progressKey) {
          setProgressSteps((prev) =>
            _activateProgressStep(prev, progressKey, _progressLabel(t, progressKey), 'waiting'),
          );
        }
      },
      onSuggestions: (event) => {
        setFollowUpSuggestions(event.suggestions);
      },
      onToolCall: (event) => {
        const progressKey = _progressKeyFromTool(event.tool);
        if (event.status === 'calling') {
          setActiveToolCall(event.tool);
          if (progressKey) {
            setProgressSteps((prev) =>
              _activateProgressStep(prev, progressKey, _progressLabel(t, progressKey), 'in_progress'),
            );
          }
        } else {
          setActiveToolCall(null);
          if (progressKey) {
            setProgressSteps((prev) => _completeProgressStep(prev, progressKey));
          }
        }
      },
      onDone: (event: SSEDoneEvent) => {
        setActiveToolCall(null);
        setProgressSteps((prev) => prev.map((step) => ({ ...step, status: 'completed' as const })));
        if (event.conversation_id && !activeConversationId) {
          setActiveConversationId(event.conversation_id);
        }
      },
      onError: (message) => {
        setActiveToolCall(null);
        setProgressSteps((prev) => prev.map((step) => ({ ...step, status: 'completed' as const })));
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: `❌ Error: ${message}`, timestamp: new Date().toISOString() },
        ]);
      },
    });

  // When streaming finishes, commit the streamed content as a message
  useEffect(() => {
    if (!isStreaming && streamingContent) {
      setMessages((prev) => {
        const filtered = prev.filter((m) => !m.isStreaming);
        return [
          ...filtered,
          { role: 'assistant', content: streamingContent, timestamp: new Date().toISOString() },
        ];
      });
      setStreamingContent('');
    }
  }, [isStreaming, streamingContent, setStreamingContent]);

  // While streaming, show a live message
  useEffect(() => {
    if (isStreaming && streamingContent) {
      setMessages((prev) => {
        const filtered = prev.filter((m) => !m.isStreaming);
        return [
          ...filtered,
          { role: 'assistant', content: streamingContent, isStreaming: true, timestamp: new Date().toISOString() },
        ];
      });
    }
  }, [isStreaming, streamingContent]);

  const toggleOpen = useCallback(() => setIsOpen((v) => !v), []);

  const loadConversations = useCallback(async () => {
    try {
      const res = await listAgentConversations();
      setConversations(res.data || []);
    } catch {
      // ignore on error
    }
  }, []);

  const handleDeleteConversation = useCallback(
    async (id: string) => {
      try {
        await deleteAgentConversation(id);
        setConversations((prev) => prev.filter((c) => c.id !== id));
        if (activeConversationId === id) {
          setActiveConversationId(null);
          setMessages([]);
        }
      } catch {
        // ignore
      }
    },
    [activeConversationId],
  );

  const startNewConversation = useCallback(() => {
    setActiveConversationId(null);
    setMessages([]);
    setPendingCard(null);
    setProgressSteps([]);
    setFollowUpSuggestions([]);
  }, []);

  // Load messages when conversation changes
  useEffect(() => {
    if (!activeConversationId) return;
    setFollowUpSuggestions([]);
    (async () => {
      try {
        const res = await getAgentConversation(activeConversationId);
        const conv = res.data;
        if (conv?.messages) {
          const loaded: AgentMessage[] = [];
          for (const m of conv.messages as Array<Record<string, unknown>>) {
            const role = m.role as string;
            const content = m.content as string;
            const ts = m.timestamp as string;

            // Detect card_response JSON in user messages from DB
            if (role === 'user' && content) {
              const parsed = _parseCardResponseContent(content);
              if (parsed) {
                loaded.push({ role: 'user', content: parsed.summary, timestamp: ts });
                continue;
              }
            }

            loaded.push({ role: role as AgentMessage['role'], content, timestamp: ts });
          }
          setMessages(loaded);
        }
      } catch {
        // ignore
      }
    })();
  }, [activeConversationId]);

  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || isStreaming) return;

      setMessages((prev) => [
        ...prev,
        { role: 'user', content: text, timestamp: new Date().toISOString() },
      ]);
      setPendingCard(null);
      setProgressSteps([
        {
          key: 'understanding_request',
          label: _progressLabel(t, 'understanding_request'),
          status: 'in_progress',
        },
      ]);
      setFollowUpSuggestions([]);

      const url = `${API_BASE_URL}/agent/chat`;
      await startStream(url, {
        message: text,
        conversation_id: activeConversationId,
        language: i18n.language,
      });

      loadConversations();
    },
    [isStreaming, activeConversationId, startStream, loadConversations, i18n.language],
  );

  const handleCardResponse = useCallback(
    async (cardId: string, cardType: string, selection: Record<string, unknown>) => {
      if (!activeConversationId || isStreaming) return;

      // Preserve the resolved card + user selection in the conversation history
      if (pendingCard) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant' as const,
            resolvedCard: {
              cardType: pendingCard.card_type,
              data: pendingCard.data as Record<string, unknown>,
              selection,
            },
            timestamp: new Date().toISOString(),
          },
        ]);
      }
      const progressKey = _progressKeyFromCard(cardType);
      if (progressKey) {
        setProgressSteps((prev) => _completeProgressStep(prev, progressKey));
      }
      setPendingCard(null);
      setFollowUpSuggestions([]);

      // Add a user message summarizing the selection
      const summary = _formatCardSelection(cardType, selection);
      if (summary) {
        setMessages((prev) => [
          ...prev,
          { role: 'user', content: summary, timestamp: new Date().toISOString() },
        ]);
      }

      const url = `${API_BASE_URL}/agent/card-response`;
      await startStream(url, {
        conversation_id: activeConversationId,
        card_id: cardId,
        card_type: cardType,
        selection,
        language: i18n.language,
      });
    },
    [activeConversationId, isStreaming, startStream, pendingCard, i18n.language],
  );

  return (
    <AgentChatContext.Provider
      value={{
        isOpen,
        toggleOpen,
        setIsOpen,
        conversations,
        activeConversationId,
        setActiveConversationId,
        loadConversations,
        deleteConversation: handleDeleteConversation,
        startNewConversation,
        messages,
        isStreaming,
        activeToolCall,
        progressSteps,
        sendMessage,
        sendCardResponse: handleCardResponse,
        stopStreaming: stopStream,
        pendingCard,
        followUpSuggestions,
      }}
    >
      {children}
    </AgentChatContext.Provider>
  );
}

export function useAgentChat() {
  const ctx = useContext(AgentChatContext);
  if (!ctx) throw new Error('useAgentChat must be used within AgentChatProvider');
  return ctx;
}
